import { useState } from "react";
import { Mail, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * First-login email consent banner shown to users who registered before the
 * explicit-opt-in flow existed. Once the user answers (yes or no), the backend
 * stamps ``email_consent_recorded_at`` and this component never renders again
 * for them.
 */
export default function EmailConsentBanner() {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);

  const shouldShow =
    user &&
    typeof user === "object" &&
    !user.is_ephemeral &&
    !user.email_consent_recorded_at &&
    // Wait for the beta acknowledgment modal to be resolved first so we
    // never stack two blocking prompts on top of each other.
    (user.beta_ack_version_accepted ?? 0) >= (user.beta_ack_required_version ?? 1) &&
    // Don't block a reader who arrived via a "Review the agreement" link.
    window.location.hash !== "#confidentiality";

  const respond = async (optIn) => {
    setBusy(true);
    try {
      await api.post("/users/me/email-consent", { opt_in: optIn });
      await refresh();
      toast.success(
        optIn
          ? "You're subscribed to email updates."
          : "No problem — we won't email you updates.",
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!shouldShow) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[96] flex items-end sm:items-center justify-center p-4"
      data-testid="email-consent-banner"
    >
      <div className="absolute inset-0 bg-foreground/45 backdrop-blur-[2px]" />
      <div className="relative max-w-md w-full rounded-2xl border border-border bg-[hsl(var(--sb-cream))] shadow-xl p-6 sm:p-7">
        <div className="inline-flex items-center gap-2">
          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[hsl(var(--sb-forest))]/12 text-[hsl(var(--sb-forest-2))]">
            <Mail size={16} />
          </span>
          <span className="text-[11px] uppercase tracking-[0.16em] font-medium text-[hsl(var(--sb-forest-2))]">
            Email preferences
          </span>
        </div>
        <h2 className="mt-4 font-serif text-2xl leading-tight" data-testid="email-consent-title">
          Can we send you occasional updates?
        </h2>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
          We're introducing email updates about new features, tips, and important
          announcements. Nothing spammy — a short note when something meaningful
          ships. You can change this any time from Settings → Email Preferences.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row-reverse gap-2">
          <button
            type="button"
            onClick={() => respond(true)}
            disabled={busy}
            className="btn-primary flex-1 sm:flex-none"
            data-testid="email-consent-optin"
          >
            <Check size={14} /> Yes, keep me posted
          </button>
          <button
            type="button"
            onClick={() => respond(false)}
            disabled={busy}
            className="btn-ghost border flex-1 sm:flex-none"
            data-testid="email-consent-optout"
          >
            <XIcon size={14} /> No thanks
          </button>
        </div>
      </div>
    </div>
  );
}
